/**
 * GTM / Google Ads E-Commerce Analytics Utility
 *
 * Pushes GA4-compatible ecommerce events to window.dataLayer, which is then
 * picked up by Google Tag Manager (GTM-KLZ5GNWT) and forwarded to
 * Google Ads (AW-1071697805) and GA4 (G-W1TJ6B8SM7).
 *
 * All monetary values are in AUD.
 */

declare global {
  interface Window {
    dataLayer: Record<string, unknown>[];
  }
}

/** Clears the previous ecommerce object then pushes a new event. */
function pushToDataLayer(event: Record<string, unknown>): void {
  if (typeof window === "undefined") return;
  window.dataLayer = window.dataLayer || [];
  // GA4 requires clearing previous ecommerce data before each push
  window.dataLayer.push({ ecommerce: null });
  window.dataLayer.push(event);
}

// ---------------------------------------------------------------------------
// Item builder helpers
// ---------------------------------------------------------------------------

/** Build a GA4-compatible item object from a full product (product page). */
function buildProductItem(
  product: any,
  quantity: number,
  unitPrice: number,
): Record<string, unknown> {
  return {
    item_id: product?.overview?.code || product?._id || "",
    item_name: product?.overview?.name || "",
    item_category:
      product?.globalProductCategory?.name ||
      product?.category?.name ||
      product?.subCategory?.name ||
      "",
    price: unitPrice,
    quantity,
  };
}

/** Build a GA4-compatible item object from a cart item. */
function buildCartItem(item: any): Record<string, unknown> {
  return {
    item_id:
      item.productId?.overview?.code ||
      item.productId?._id ||
      item.product_Id ||
      "",
    item_name: item.productId?.overview?.name || "",
    item_category:
      item.productId?.globalProductCategory?.name ||
      item.productId?.category?.name ||
      "",
    price: item.calculatedPrice?.unitPrice ?? item.price ?? 0,
    quantity: item.quantity ?? 1,
  };
}

// ---------------------------------------------------------------------------
// Product page events
// ---------------------------------------------------------------------------

/**
 * Fire when a product page finishes loading and the price is computed.
 * Corresponds to GA4 `view_item`.
 */
export function trackViewItem(product: any, unitPrice: number): void {
  if (!product) return;
  pushToDataLayer({
    event: "view_item",
    ecommerce: {
      currency: "AUD",
      value: unitPrice,
      items: [buildProductItem(product, 1, unitPrice)],
    },
  });
}

/**
 * Fire immediately when the user successfully adds a product to the cart.
 * Corresponds to GA4 `add_to_cart`.
 */
export function trackAddToCart(
  product: any,
  quantity: number,
  unitPrice: number,
  grandTotal: number,
): void {
  if (!product) return;
  pushToDataLayer({
    event: "add_to_cart",
    ecommerce: {
      currency: "AUD",
      value: grandTotal,
      items: [buildProductItem(product, quantity, unitPrice)],
    },
  });
}

// ---------------------------------------------------------------------------
// Cart page events
// ---------------------------------------------------------------------------

/**
 * Fire when the Cart page mounts with items loaded.
 * Corresponds to GA4 `view_cart`.
 */
export function trackViewCart(cartItems: any[], total: number): void {
  if (!cartItems?.length) return;
  pushToDataLayer({
    event: "view_cart",
    ecommerce: {
      currency: "AUD",
      value: total,
      items: cartItems.map(buildCartItem),
    },
  });
}

/**
 * Fire when the user removes an item from the cart.
 * Corresponds to GA4 `remove_from_cart`.
 */
export function trackRemoveFromCart(item: any): void {
  if (!item) return;
  pushToDataLayer({
    event: "remove_from_cart",
    ecommerce: {
      currency: "AUD",
      value: item.calculatedPrice?.totalPrice ?? 0,
      items: [buildCartItem(item)],
    },
  });
}

/**
 * Fire when the user clicks "Proceed to Checkout" from the Cart page.
 * Corresponds to GA4 `begin_checkout`.
 */
export function trackBeginCheckout(
  cartItems: any[],
  total: number,
  userType?: string,
): void {
  if (!cartItems?.length) return;
  pushToDataLayer({
    event: "begin_checkout",
    ecommerce: {
      currency: "AUD",
      value: total,
      items: cartItems.map(buildCartItem),
    },
    user_type: userType || "Non-VIP",
  });
}

// ---------------------------------------------------------------------------
// Checkout funnel events
// ---------------------------------------------------------------------------

/**
 * Fire when the user submits the address/contact form (step 0 → 1).
 * Corresponds to GA4 `add_shipping_info`.
 */
export function trackAddShippingInfo(
  cartItems: any[],
  total: number,
  shippingCost: number,
): void {
  if (!cartItems?.length) return;
  pushToDataLayer({
    event: "add_shipping_info",
    ecommerce: {
      currency: "AUD",
      value: total,
      shipping: shippingCost,
      items: cartItems.map(buildCartItem),
    },
  });
}

/**
 * Fire when the user reaches the Stripe payment step (step 1).
 * Corresponds to GA4 `add_payment_info`.
 */
export function trackAddPaymentInfo(
  cartItems: any[],
  total: number,
  couponCode?: string,
): void {
  if (!cartItems?.length) return;
  pushToDataLayer({
    event: "add_payment_info",
    ecommerce: {
      currency: "AUD",
      value: total,
      payment_type: "Stripe / Credit",
      coupon: couponCode || "",
      items: cartItems.map(buildCartItem),
    },
  });
}

/**
 * Fire when an order is confirmed (step 2 / PaymentStatus success).
 * Corresponds to GA4 `purchase` and fires Google Ads conversion.
 */
export function trackPurchase(
  orderId: string,
  cartItems: any[],
  total: number,
  shippingCost: number,
  couponCode?: string,
  userType?: string,
): void {
  if (!orderId) return;
  pushToDataLayer({
    event: "purchase",
    ecommerce: {
      transaction_id: orderId,
      currency: "AUD",
      value: total,
      shipping: shippingCost,
      coupon: couponCode || "",
      items: cartItems.map(buildCartItem),
    },
    user_type: userType || "Non-VIP",
  });
}
